import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import type { Id } from '../../../../convex/_generated/dataModel';
import { ReportingModal } from './ReportingModal';

export function PublicReferencePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showReportModal, setShowReportModal] = useState(false);

  // Fetch the public reference
  const reference = useQuery(
    api.references.getPublicReference,
    id ? { referenceId: id as Id<'references'> } : 'skip'
  );

  // Mutation for liking a reference
  const likeReference = useMutation(api.references.likePublicReference);

  const handleLike = async () => {
    if (!id) return;
    try {
      await likeReference({ referenceId: id as Id<'references'> });
    } catch (error) {
      console.error('Failed to like reference:', error);
    }
  };

  const handleBack = () => {
    navigate('/public');
  };

  if (reference === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  if (reference === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Reference Not Found</h1>
          <p className="mb-4">This reference may have been removed or is no longer public.</p>
          <button onClick={handleBack} className="btn btn-primary">
            Back to Directory
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-100">
      {/* Header */}
      <div className="bg-yellow-300 border-b-2 border-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="btn btn-ghost btn-sm shadow-comic-sm border-2 border-gray-800 text-gray-800 hover:bg-white"
            >
              ← Back to Directory
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowReportModal(true)}
                className="btn btn-outline btn-sm shadow-comic-sm border-2 border-gray-800 text-gray-800 hover:bg-white"
              >
                🚩 Report
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="cutting-mat-bg-subtle min-h-screen">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="card bg-yellow-300 shadow-comic border-2 border-gray-800">
            <div className="card-body">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-3xl font-bold mb-2">{reference.title}</h1>
              <div className="flex items-center gap-4 text-sm">
                <div className="badge badge-secondary">{reference.type}</div>
                {reference.category && (
                  <div className="badge badge-outline">{reference.category}</div>
                )}
                {reference.author?.displayName && (
                  <span className="text-base-content/70">
                    by {reference.author.displayName}
                  </span>
                )}
              </div>
            </div>

            {/* Media */}
            {reference.imageUrl && (
              <div className="mb-6">
                <img
                  src={reference.imageUrl}
                  alt={reference.title}
                  className="w-full rounded-lg shadow-lg"
                />
              </div>
            )}

            {/* Description */}
            {reference.description && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">Description</h3>
                <p className="text-base-content/80 whitespace-pre-wrap">
                  {reference.description}
                </p>
              </div>
            )}

            {/* Source URL */}
            {reference.sourceURL && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">Source</h3>
                <a
                  href={reference.sourceURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link link-primary break-all"
                >
                  {reference.sourceURL}
                </a>
              </div>
            )}

            {/* Tags */}
            {reference.tags && reference.tags.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {reference.tags.map((tag) => tag && (
                    <div key={tag._id} className="badge badge-outline">
                      {tag.name}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Stats and Actions */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center gap-4 text-sm text-base-content/70">
                <span>👁 {reference.publicMetadata?.views || 0} views</span>
                <span>❤️ {reference.publicMetadata?.likes || 0} likes</span>
                <span>
                  Published {new Date(reference.publicMetadata?.publishedAt || 0).toLocaleDateString()}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={handleLike}
                  className="btn btn-outline btn-sm"
                >
                  ❤️ Like
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

      {/* Reporting Modal */}
      {/* Note: ReportingModal component would need to be created separately */}
      {/* {reference && (
        <ReportingModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          referenceId={reference._id}
        />
      )} */}
    </div>
  );
}